import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable
  }
}

interface InvoicePDFOptions {
  invoice: {
    id: string
    invoiceNumber: string
    issueDate: string
    dueDate?: string
    status: string
    totalAmount: number
    balanceDue: number
    currency: string
    subtotal?: number
    taxAmount?: number
    discountAmount?: number
    customer?: {
      name: string
      email?: string
      address?: string
      phone?: string
      taxId?: string
    }
    lines?: Array<{
      description: string
      quantity: number
      unitPrice: number
      lineTotal: number
      taxRate?: number
      discountRate?: number
      discountAmount?: number
      taxAmount?: number
    }>
    notes?: string
    paymentUrl?: string
  }
  company: {
    name: string
    logoUrl?: string
    primaryColor?: string
    address?: string
    city?: string
    state?: string
    postalCode?: string
    email?: string
    phone?: string
    website?: string
    taxId?: string
    invoiceTerms?: string
    invoiceFooter?: string
  }
}

export class InvoicePDFGenerator {
  private doc: jsPDF
  private primaryColor: string
  private margin = 20
  private currentY = 20

  constructor(private options: InvoicePDFOptions) {
    this.doc = new jsPDF('p', 'mm', 'a4')
    this.primaryColor = options.company.primaryColor || '#1f2937'
  }

  private formatCurrency(amount: number, currency: string): string {
    // Always use Frw format for Rwandan Francs
    if (currency === 'Frw' || currency === 'FRW' || currency === 'RWF') {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw`
    }
    // For other currencies, try standard formatting
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount)
    } catch {
      return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
    }
  }

  private setColor(color: string) {
    // Convert hex to RGB
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    this.doc.setTextColor(r, g, b)
  }

  private resetColor() {
    this.doc.setTextColor(0, 0, 0)
  }

  private addLine(y?: number) {
    const lineY = y || this.currentY
    this.doc.setDrawColor(200, 200, 200)
    this.doc.line(this.margin, lineY, 190, lineY)
  }

  private generateBarcode(): string {
    // Generate scannable Code39 barcode with invoice information
    const canvas = document.createElement('canvas')
    canvas.width = 150
    canvas.height = 40
    const ctx = canvas.getContext('2d')
    
    if (!ctx) return ''
    
    // White background
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Create barcode data - use invoice number as primary identifier
    // Remove "INV-" or "POS-" prefix if present, keep only the numeric/alphanumeric part
    let barcodeData = (this.options.invoice.invoiceNumber || 'INV001').toUpperCase()
    // Remove "INV-" or "POS-" prefix if it exists
    barcodeData = barcodeData.replace(/^(INV|POS)-?/i, '')
    // Remove any characters not supported by Code39
    barcodeData = barcodeData.replace(/[^0-9A-Z\s\-\.\$\/\+\%]/g, '')
    // Limit length to fit on canvas
    if (barcodeData.length > 15) {
      barcodeData = barcodeData.substring(0, 15)
    }
    
    // Code39 character encoding (each character = 9 elements: 5 bars, 4 spaces)
    const code39Pattern: { [key: string]: string } = {
      '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
      '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
      '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', 'A': 'wnnnnwnnw', 'B': 'nnwnnwnnw',
      'C': 'wnwnnwnnn', 'D': 'nnnnwwnnw', 'E': 'wnnnwwnnn', 'F': 'nnwnwwnnn',
      'G': 'nnnnnwwnw', 'H': 'wnnnnwwnn', 'I': 'nnwnnwwnn', 'J': 'nnnnwwwnn',
      'K': 'wnnnnnnww', 'L': 'nnwnnnnww', 'M': 'wnwnnnnwn', 'N': 'nnnnwnnww',
      'O': 'wnnnwnnwn', 'P': 'nnwnwnnwn', 'Q': 'nnnnnnwww', 'R': 'wnnnnnwwn',
      'S': 'nnwnnnwwn', 'T': 'nnnnwnwwn', 'U': 'wwnnnnnnw', 'V': 'nwwnnnnnw',
      'W': 'wwwnnnnnn', 'X': 'nwnnwnnnw', 'Y': 'wwnnwnnnn', 'Z': 'nwwnwnnnn',
      '-': 'nwnnnnwnw', '.': 'wwnnnnwnn', ' ': 'nwwnnnwnn', '$': 'nwnwnwnnn',
      '/': 'nwnnnwnwn', '+': 'nwnwnnnwn', '%': 'nnnwnwnwn'
    }
    
    const barHeight = 30
    const narrowBar = 1.5  // Narrow bar/space width
    const wideBar = 3.5    // Wide bar/space width (2.3x narrow)
    let currentX = 10
    
    // Helper function to render a Code39 pattern (alternates bars and spaces)
    const renderPattern = (pattern: string, x: number): number => {
      let pos = x
      for (let i = 0; i < pattern.length; i++) {
        const isWide = pattern[i] === 'w'
        const width = isWide ? wideBar : narrowBar
        
        if (i % 2 === 0) {
          // Even positions are bars - draw them
          ctx.fillStyle = this.primaryColor
          ctx.fillRect(pos, 5, width, barHeight)
        }
        // Odd positions are spaces - just advance position (already white background)
        pos += width
      }
      return pos
    }
    
    // Start character (*) - pattern: nwnnwnwnn
    const startPattern = 'nwnnwnwnn'
    currentX = renderPattern(startPattern, currentX)
    
    // Character gap (inter-character space)
    currentX += narrowBar
    
    // Encode each character
    for (let charIndex = 0; charIndex < barcodeData.length && currentX < canvas.width - 20; charIndex++) {
      const char = barcodeData[charIndex]
      const pattern = code39Pattern[char] || code39Pattern['0']
      
      currentX = renderPattern(pattern, currentX)
      
      // Character gap (inter-character space)
      if (charIndex < barcodeData.length - 1) {
        currentX += narrowBar
      }
    }
    
    // Stop character (*)
    currentX += narrowBar // Gap before stop character
    renderPattern(startPattern, currentX)
    
    // Display barcode text below for human readability
    ctx.fillStyle = '#000000'
    ctx.font = '8px Arial'
    ctx.textAlign = 'center'
    ctx.fillText(barcodeData, canvas.width / 2, canvas.height - 2)
    
    return canvas.toDataURL()
  }

  private async addHeader() {
    const { company, invoice } = this.options
    
    // Professional logo and company name (same style as journal entry)
    let logoAdded = false;
    const logoSize = 18;
    const logoY = this.currentY;

    if (company.logoUrl) {
      try {
        const logoUrl = company.logoUrl.toLowerCase();

        if (logoUrl.includes('.jpg') || logoUrl.includes('.jpeg')) {
          this.doc.addImage(company.logoUrl, 'JPEG', this.margin, logoY, logoSize, logoSize)
          logoAdded = true;
        } else if (logoUrl.includes('.png')) {
          this.doc.addImage(company.logoUrl, 'PNG', this.margin, logoY, logoSize, logoSize)
          logoAdded = true;
        } else {
          try {
            this.doc.addImage(company.logoUrl, 'PNG', this.margin, logoY, logoSize, logoSize)
            logoAdded = true;
          } catch (pngError) {
            try {
              this.doc.addImage(company.logoUrl, 'JPEG', this.margin, logoY, logoSize, logoSize)
              logoAdded = true;
            } catch (jpegError) {
              // Continue without logo
            }
          }
        }
      } catch (error) {
        logoAdded = false;
      }
    }

    // Company name aligned with logo center
    const companyNameY = logoY + (logoSize / 2) + 2;
    const companyX = logoAdded ? this.margin + logoSize + 2 : this.margin;
    
    this.doc.setFontSize(18)
    this.doc.setTextColor(0, 0, 0)
    this.doc.setFont(undefined, 'bold')
    this.doc.text(company.name, companyX, companyNameY)
    this.doc.setFont(undefined, 'normal')
    
    // Company website next to name
    if (company.website) {
      const nameWidth = this.doc.getTextWidth(company.name)
      this.doc.setFontSize(10)
      this.doc.setTextColor(120, 120, 120)
      this.doc.text('•', companyX + nameWidth + 2, companyNameY)
      this.doc.text(company.website, companyX + nameWidth + 5, companyNameY)
    }

    // Company details (keep existing layout)
    this.resetColor()
    this.doc.setFontSize(10)
    let detailsY = logoY + logoSize + 8
    
    if (company.address) {
      this.doc.text(company.address, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }
    
    if (company.city || company.state || company.postalCode) {
      const location = [company.city, company.state, company.postalCode].filter(Boolean).join(', ')
      this.doc.text(location, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }
    
    if (company.email) {
      this.doc.text(`Email: ${company.email}`, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }
    
    if (company.phone) {
      this.doc.text(`Phone: ${company.phone}`, logoAdded ? this.margin + logoSize + 2 : this.margin, detailsY)
      detailsY += 4
    }

    // Invoice header on the right (aligned with logo)
    this.doc.setFontSize(28)
    this.setColor(this.primaryColor)
    this.doc.text('INVOICE', 190 - this.doc.getTextWidth('INVOICE'), logoY + 10)
    
    this.resetColor()
    this.doc.setFontSize(16)
    // Calculate invoice number position - use this as reference for barcode alignment
    const invoiceNumberText = `#${invoice.invoiceNumber}`
    const invoiceNumberX = 190 - this.doc.getTextWidth(invoiceNumberText)
    const invoiceNumberY = logoY + 20
    this.doc.text(invoiceNumberText, invoiceNumberX, invoiceNumberY)
    
    // Status badge (aligned with invoice header)
    this.doc.setFontSize(10)
    const statusText = invoice.status.toUpperCase()
    const statusWidth = this.doc.getTextWidth(statusText) + 4
    
    // Status background
    this.doc.setFillColor(240, 240, 240)
    this.doc.rect(190 - statusWidth, logoY + 25, statusWidth, 6, 'F')
    
    this.doc.text(statusText, 190 - statusWidth + 2, logoY + 29)

    // Add Barcode - aligned with the same left margin as invoice number
    try {
      const barcode = this.generateBarcode()
      if (barcode) {
        // Align barcode with invoice number (same left margin)
        // Barcode width is approximately 60mm, positioned at invoice number X
        const barcodeWidth = 60 // mm
        const barcodeHeight = 8 // mm
        const barcodeY = logoY + 35 // Position below invoice number
        this.doc.addImage(barcode, 'PNG', invoiceNumberX, barcodeY, barcodeWidth, barcodeHeight)
      }
    } catch (error) {
      // Continue without barcode
    }

    this.currentY = Math.max(detailsY, logoY + 60)
    this.addLine(this.currentY)
    this.currentY += 10
  }

  private addInvoiceDetails() {
    const { invoice } = this.options
    
    // Bill To section
    this.doc.setFontSize(12)
    this.doc.setFont('helvetica', 'bold')
    this.doc.text('BILL TO', this.margin, this.currentY)
    
    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(10)
    let billToY = this.currentY + 6
    
    if (invoice.customer?.name) {
      this.doc.setFont('helvetica', 'bold')
      this.doc.text(invoice.customer.name, this.margin, billToY)
      this.doc.setFont('helvetica', 'normal')
      billToY += 5
    }
    
    if (invoice.customer?.address) {
      this.doc.text(invoice.customer.address, this.margin, billToY)
      billToY += 4
    }
    
    if (invoice.customer?.email) {
      this.doc.text(invoice.customer.email, this.margin, billToY)
      billToY += 4
    }
    
    if (invoice.customer?.phone) {
      this.doc.text(invoice.customer.phone, this.margin, billToY)
      billToY += 4
    }

    // Invoice details on the right
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(12)
    this.doc.text('INVOICE DETAILS', 110, this.currentY)
    
    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(10)
    let detailsY = this.currentY + 6
    
    const details = [
      ['Issue Date:', new Date(invoice.issueDate).toLocaleDateString()],
      ...(invoice.dueDate ? [['Due Date:', new Date(invoice.dueDate).toLocaleDateString()]] : []),
      ['Currency:', invoice.currency],
      ['Amount:', this.formatCurrency(invoice.totalAmount, invoice.currency)],
      ...(invoice.balanceDue > 0 ? [['Balance Due:', this.formatCurrency(invoice.balanceDue, invoice.currency)]] : [])
    ]
    
    details.forEach(([label, value]) => {
      this.doc.text(label, 110, detailsY)
      this.doc.text(value, 140, detailsY)
      detailsY += 4
    })

    this.currentY = Math.max(billToY, detailsY) + 10
  }

  private addLineItems() {
    const { invoice } = this.options
    
    if (!invoice.lines || invoice.lines.length === 0) return

    // Table headers
    const headers = [['Description', 'Qty', 'Rate', 'Amount']]
    
    // Check for discounts and tax
    const hasDiscount = invoice.lines.some(line => 
      (line.discountRate && line.discountRate > 0) || 
      (line.discountAmount && line.discountAmount > 0)
    ) || (invoice.discountAmount && invoice.discountAmount > 0)
    
    const hasTax = invoice.lines.some(line => 
      (line.taxRate && line.taxRate > 0) || 
      (line.taxAmount && line.taxAmount > 0)
    ) || (invoice.taxAmount && invoice.taxAmount > 0)
    
    // Add discount column if any line has discount
    if (hasDiscount) {
      headers[0].splice(3, 0, 'Discount')
    }
    
    // Add tax column if any line has tax
    if (hasTax) {
      headers[0].splice(hasDiscount ? 4 : 3, 0, 'Tax')
    }

    // Table data
    const data = invoice.lines.map(line => {
      const row = [
        line.description,
        line.quantity.toString(),
        this.formatCurrency(line.unitPrice, invoice.currency),
        this.formatCurrency(line.lineTotal, invoice.currency)
      ]
      
      // Insert discount column
      if (hasDiscount) {
        const discountRate = line.discountRate || 0
        const discountAmount = line.discountAmount || 0
        const discountText = discountRate > 0 
          ? `${discountRate}%` 
          : discountAmount > 0 
            ? this.formatCurrency(discountAmount, invoice.currency)
            : '—'
        row.splice(3, 0, discountText)
      }
      
      // Insert tax column
      if (hasTax) {
        const taxRate = line.taxRate || 0
        const taxAmount = line.taxAmount || 0
        const taxText = taxRate > 0 
          ? `${taxRate}%` 
          : taxAmount > 0 
            ? this.formatCurrency(taxAmount, invoice.currency)
            : '—'
        row.splice(hasDiscount ? 4 : 3, 0, taxText)
      }
      
      return row
    })

    // Generate table with proper column widths to prevent overlapping - increased table width
    autoTable(this.doc, {
      head: headers,
      body: data,
      startY: this.currentY,
      margin: { left: this.margin, right: 20 }, // Reduced right margin to allow wider table
      styles: {
        fontSize: 9,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [31, 41, 55], // primaryColor equivalent
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' }, // Description - flexible width
        1: { cellWidth: 25, halign: 'center' }, // Qty - increased width
        2: { cellWidth: 40, halign: 'right' }, // Rate - increased width for currency
        ...(hasDiscount && hasTax 
          ? { 
              3: { cellWidth: 30, halign: 'center' }, // Discount - increased width
              4: { cellWidth: 30, halign: 'center' }, // Tax - increased width
              5: { cellWidth: 40, halign: 'right' } // Amount - increased width
            }
          : hasDiscount 
            ? { 
                3: { cellWidth: 30, halign: 'center' }, // Discount - increased width
                4: { cellWidth: 40, halign: 'right' } // Amount - increased width
              }
            : hasTax 
              ? { 
                  3: { cellWidth: 30, halign: 'center' }, // Tax - increased width
                  4: { cellWidth: 40, halign: 'right' } // Amount - increased width
                }
              : { 
                  3: { cellWidth: 40, halign: 'right' } // Amount - increased width
                })
      }
    })

    this.currentY = (this.doc as any).lastAutoTable.finalY + 10
  }

  private addTotals() {
    const { invoice } = this.options
    
    // Calculate subtotal from line items (before discounts and tax)
    const subtotal = invoice.subtotal || invoice.lines?.reduce((sum, line) => {
      const lineSubtotal = Number(line.quantity) * Number(line.unitPrice)
      return sum + lineSubtotal
    }, 0) || 0
    
    // Calculate total discount (invoice-level or from line items)
    let discount = invoice.discountAmount || 0
    if (discount === 0 && invoice.lines) {
      discount = invoice.lines.reduce((sum, line) => {
        const discountRate = Number(line.discountRate) || 0
        const discountAmount = Number(line.discountAmount) || 0
        
        if (discountAmount > 0) {
          return sum + discountAmount
        } else if (discountRate > 0) {
          const lineSubtotal = Number(line.quantity) * Number(line.unitPrice)
          return sum + (lineSubtotal * (discountRate / 100))
        }
        return sum
      }, 0)
    }
    
    // Calculate total tax (invoice-level or from line items)
    let tax = invoice.taxAmount || 0
    if (tax === 0 && invoice.lines) {
      tax = invoice.lines.reduce((sum, line) => {
        const taxRate = Number(line.taxRate) || 0
        const taxAmount = Number(line.taxAmount) || 0
        
        if (taxAmount > 0) {
          return sum + taxAmount
        } else if (taxRate > 0) {
          const lineSubtotal = Number(line.quantity) * Number(line.unitPrice)
          const lineDiscount = Number(line.discountAmount) || (lineSubtotal * (Number(line.discountRate) || 0) / 100)
          const taxableAmount = lineSubtotal - lineDiscount
          return sum + (taxableAmount * (taxRate / 100))
        }
        return sum
      }, 0)
    }
    
    const totalsX = 130
    const labelX = 110
    
    // Subtotal
    this.doc.setFontSize(10)
    this.doc.text('Subtotal:', labelX, this.currentY)
    this.doc.text(this.formatCurrency(subtotal, invoice.currency), totalsX, this.currentY)
    this.currentY += 5
    
    // Discount
    if (discount > 0) {
      this.doc.text('Discount:', labelX, this.currentY)
      this.doc.text(`-${this.formatCurrency(discount, invoice.currency)}`, totalsX, this.currentY)
      this.currentY += 5
    }
    
    // Tax
    if (tax > 0) {
      this.doc.text('Tax:', labelX, this.currentY)
      this.doc.text(this.formatCurrency(tax, invoice.currency), totalsX, this.currentY)
      this.currentY += 5
    }
    
    // Line above total
    this.doc.setDrawColor(100, 100, 100)
    this.doc.line(labelX, this.currentY + 2, 185, this.currentY + 2)
    this.currentY += 8
    
    // Total
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(12)
    this.setColor(this.primaryColor)
    this.doc.text('TOTAL:', labelX, this.currentY)
    this.doc.text(this.formatCurrency(invoice.totalAmount, invoice.currency), totalsX, this.currentY)
    
    this.resetColor()
    this.currentY += 10
    
    // Balance due
    if (invoice.balanceDue > 0) {
      this.doc.setFont('helvetica', 'bold')
      this.doc.setFontSize(11)
      this.doc.setTextColor(220, 38, 38) // Red color
      this.doc.text('BALANCE DUE:', labelX, this.currentY)
      // Move amount further right to avoid collision
      const balanceAmountX = totalsX + 10 // Add extra spacing
      this.doc.text(this.formatCurrency(invoice.balanceDue, invoice.currency), balanceAmountX, this.currentY)
      this.resetColor()
      this.currentY += 8
    }
  }

  private addNotes() {
    const { invoice } = this.options
    
    if (invoice.notes) {
      this.currentY += 5
      this.doc.setFont('helvetica', 'bold')
      this.doc.setFontSize(11)
      this.doc.text('NOTES:', this.margin, this.currentY)
      
      this.doc.setFont('helvetica', 'normal')
      this.doc.setFontSize(10)
      this.currentY += 6
      
      const lines = this.doc.splitTextToSize(invoice.notes, 170)
      this.doc.text(lines, this.margin, this.currentY)
      this.currentY += lines.length * 4 + 5
    }
  }

  private addFooter() {
    const { company } = this.options
    
    // Payment terms
    this.currentY += 10
    this.addLine(this.currentY)
    this.currentY += 8
    
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(11)
    this.doc.text('PAYMENT TERMS', this.margin, this.currentY)
    
    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(9)
    this.currentY += 6
    
    const terms = company.invoiceTerms || 'Payment is due within 30 days of invoice date. Late payments may incur additional fees.'
    const termLines = this.doc.splitTextToSize(terms, 170)
    this.doc.text(termLines, this.margin, this.currentY)
    this.currentY += termLines.length * 3 + 5
    
    // Footer text
    if (company.invoiceFooter) {
      this.currentY += 5
      this.doc.setFontSize(8)
      this.doc.setTextColor(100, 100, 100)
      const footerLines = this.doc.splitTextToSize(company.invoiceFooter, 170)
      this.doc.text(footerLines, this.margin, this.currentY)
      this.currentY += footerLines.length * 3
    }
    
    // Generation timestamp
    this.currentY += 5
    this.doc.setFontSize(7)
    this.doc.setTextColor(150, 150, 150)
    const timestamp = `Generated on ${new Date().toLocaleString()} • Invoice #${this.options.invoice.invoiceNumber}`
    this.doc.text(timestamp, 105 - this.doc.getTextWidth(timestamp) / 2, this.currentY)
  }

  public async generate(): Promise<Blob> {
    await this.addHeader()
    this.addInvoiceDetails()
    this.addLineItems()
    this.addTotals()
    this.addNotes()
    this.addFooter()
    
    return this.doc.output('blob')
  }

  public async download(filename?: string): Promise<void> {
    await this.addHeader()
    this.addInvoiceDetails()
    this.addLineItems()
    this.addTotals()
    this.addNotes()
    this.addFooter()
    
    const defaultFilename = `invoice-${this.options.invoice.invoiceNumber}.pdf`
    this.doc.save(filename || defaultFilename)
  }
}
